import type { FC } from "react";
import { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "store/hooks";
import { getUserEmail, selectUserAddressing, selectUserName } from "store/slices/user";

import type { UserTutoringPracticeByIdFragment } from "api/generated";
import VideoWithStatus from "components/Video/VideoWithStatus";
import { getProgress } from "utils/common/video";

interface Props {
  tutoring: UserTutoringPracticeByIdFragment;
  videoTips: Array<{
    videoId: string;
    previewImage?: string | null | undefined;
  }>;
  goNextStep: (newStep: "practice" | "evaluation") => void;
}

const TutoringWelcome: FC<Props> = ({ tutoring, videoTips, goNextStep }) => {
  const { t } = useTranslation(["tutoring"]);
  const userVocativeName = useAppSelector(selectUserAddressing);
  const userName = useAppSelector(selectUserName);
  const userEmail = useAppSelector(getUserEmail);

  const name = userVocativeName ?? userName;

  const [, setVideoStatus] = useState<Record<string, boolean>>({});
  const [canContinue, setCanContinue] = useState(false);

  useEffect(() => {
    if (!userEmail) return;

    if (!videoTips.length) {
      setCanContinue(true);
      return;
    }

    const status: Record<string, boolean> = {};

    for (const videoId of videoTips.map(tip => tip.videoId)) {
      status[videoId] = getProgress(videoId, userEmail)?.isViewed ?? false;
    }

    setVideoStatus(status);

    const allFinished = Object.values(status).every(s => s);

    setCanContinue(allFinished);
    if (allFinished) onContinue();
  }, [userEmail, videoTips]);

  const onContinue = () => {
    goNextStep("practice");
  };

  const onVideoFinished = (videoId: string) => {
    setVideoStatus(prevStatus => {
      const newStatus = { ...prevStatus, [videoId]: true };
      const allFinished = Object.values(newStatus).every(status => status);
      setCanContinue(allFinished);

      return newStatus;
    });
  };

  return (
    <div className="tutoring__body">
      <div className="tutoring__videos">
        {videoTips.map(({ videoId, previewImage }) => (
          <div key={videoId}>
            <VideoWithStatus videoId={videoId} previewImage={previewImage ?? null} onFinished={onVideoFinished} />
          </div>
        ))}
      </div>

      <div>
        <div className="well">
          <div className="p-4 text-center d-flex flex-column align-items-center justify-content-center gap-2">
            <strong>{name ? `${name}, vítej v doučku!` : "Vítej v doučku!"}</strong>
            <div className="fs-15">{t("welcome.video_text")}</div>
            <div className="fs-15">
              {t("welcome.fixed_assignments_text", { count: tutoring.themeAssignments?.length ?? 0 })}
            </div>
            <div className="fs-15">{t("welcome.others_assignments_text")}</div>
            <div className="fs-15">{t("welcome.description")}</div>
          </div>
        </div>

        <Button variant="primary" className={"w-100 mt-3"} disabled={!canContinue} onClick={onContinue}>
          {videoTips.length === 0 && "Pokračovat"}
          {videoTips.length === 1 && "Po zhlédnutí videa můžeš pokračovat"}
          {videoTips.length > 1 && "Po zhlédnutí videí můžeš pokračovat"}
        </Button>
      </div>
    </div>
  );
};

export default TutoringWelcome;
